import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { UserContext } from '../../contexts/UserContext';
import '../../App.css';

const UserProfileDetail = () => {
  const { username, itemId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [userData, setUserData] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [userLikes, setUserLikes] = useState([]);
  const [userSaved, setUserSaved] = useState([]);

  useEffect(() => {
    const fetchUserAndContent = async () => {
      setIsLoading(true);
      try {
        console.log('Fetching data for username:', username);
        console.log('Current user:', user);
        
        // Try multiple ways to find the user data (matching UserProfile.js logic)
        let userDataResult = null;
        
        // If this is the current user's own profile
        const currentUserEmail = user?.email?.split('@')[0];
        const isOwnProfile = currentUserEmail === username;
        
        if (isOwnProfile && user?.uid) {
          console.log('Fetching own profile data');
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            userDataResult = { ...data, uid: user.uid };
            console.log('Found own profile data');
          }
        }
        
        // If not found, try username-based lookup
        if (!userDataResult) {
          console.log('Trying username-based lookup');
          const docRef = doc(db, 'users', username.toLowerCase());
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            userDataResult = docSnap.data();
            console.log('Found via username lookup');
          } else {
            console.log('Username lookup failed, trying email lookup');
            // Try email-based lookup
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', `${username}@gmail.com`));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const doc = querySnapshot.docs[0];
              userDataResult = { ...doc.data(), uid: doc.id };
              console.log('Found via email lookup');
            }
          }
        }
        
        if (!userDataResult) {
          console.log('User not found:', username);
          navigate('/404');
          return;
        }
        
        console.log('User data found:', userDataResult);
        setUserData(userDataResult);

        // Combine posts and legacy photos/videos (MATCHING UserProfile.js exactly)
        // BUT EXCLUDE SAVED ITEMS from detail feed
        const combinedItems = [];

        // Add rich posts first
        try {
          const postsRef = collection(db, 'posts');
          const postsQuery = query(
            postsRef,
            where('userId', '==', userDataResult.uid || username),
            where('status', '==', 'published')
          );
          
          const postsSnapshot = await getDocs(postsQuery);
          postsSnapshot.forEach(doc => {
            combinedItems.push({
              id: doc.id,
              type: 'post',
              ...doc.data()
            });
          });
          console.log('Added posts:', postsSnapshot.size);
        } catch (postsError) {
          console.log('Posts not available:', postsError.message);
        }

        // Add legacy photos - EXACT same logic as UserProfile.js
        if (Array.isArray(userDataResult.photos)) {
          const filteredPhotos = userDataResult.photos.filter(photo => photo && photo.trim() !== '');
          filteredPhotos.forEach((photo, index) => {
            combinedItems.push({
              id: `photos-${index}`, // EXACT match with UserProfile.js
              type: 'legacy-photo',
              imageUrl: photo,
              description: `Photo ${index + 1}`,
              createdAt: new Date(),
              username: username
            });
          });
          console.log('Added photos:', filteredPhotos.length);
        }

        // Add legacy videos - EXACT same logic as UserProfile.js
        if (Array.isArray(userDataResult.videos)) {
          const filteredVideos = userDataResult.videos.filter(video => video && video.trim() !== '');
          filteredVideos.forEach((video, index) => {
            combinedItems.push({
              id: `videos-${index}`, // EXACT match with UserProfile.js
              type: 'legacy-video',
              imageUrl: video,
              description: `Video ${index + 1}`,
              createdAt: new Date(),
              username: username
            });
          });
          console.log('Added videos:', filteredVideos.length);
        }

        // Add tagged items - EXACT same logic as UserProfile.js
        if (Array.isArray(userDataResult.taggedItems)) {
          const filteredTagged = userDataResult.taggedItems.filter(item => item && item.trim() !== '');
          filteredTagged.forEach((item, index) => {
            combinedItems.push({
              id: `tagged-${index}`, // EXACT match with UserProfile.js
              type: 'legacy-tagged',
              imageUrl: item,
              description: `Tagged item ${index + 1}`,
              createdAt: new Date(),
              username: username
            });
          });
          console.log('Added tagged items:', filteredTagged.length);
        }

        // REMOVED: Do not add saved items to detail feed - they're private to the user
        // and should only show in the "Saved" tab on their own profile

        // Sort by date (newest first), but keep posts at top
        combinedItems.sort((a, b) => {
          // Posts always come first
          if (a.type === 'post' && b.type !== 'post') return -1;
          if (b.type === 'post' && a.type !== 'post') return 1;
          
          // Then sort by date
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return dateB - dateA;
        });

        console.log('Total combined items:', combinedItems.length);
        console.log('Combined items:', combinedItems.map(item => ({ id: item.id, type: item.type })));
        console.log('Looking for itemId:', itemId);

        setAllItems(combinedItems);

        // Find current item index - but we'll show all items now
        const index = combinedItems.findIndex(item => item.id === itemId);
        console.log('Found index:', index);
        
        if (index !== -1) {
          setCurrentIndex(index);
          console.log('Current item index set:', index);
          
          // Scroll to the specific item after rendering
          setTimeout(() => {
            const element = document.getElementById(`item-${index}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }

        // Load user's likes and saved items if logged in
        if (user?.uid) {
          const currentUserDoc = await getDoc(doc(db, 'users', user.uid));
          if (currentUserDoc.exists()) {
            const currentUserData = currentUserDoc.data();
            setUserLikes(currentUserData.likedItems || []);
            setUserSaved(currentUserData.savedItems || []);
            console.log('Loaded user likes:', currentUserData.likedItems?.length || 0);
            console.log('Loaded user saved:', currentUserData.savedItems?.length || 0);
          }
        }

      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (username && itemId) {
      fetchUserAndContent();
    }
  }, [username, itemId, user, navigate]);

  const handleLike = async (itemId, itemOwnerId) => {
    if (!user?.uid) return;

    const isLiked = userLikes.includes(itemId);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      
      if (isLiked) {
        await updateDoc(userRef, {
          likedItems: arrayRemove(itemId)
        });
        setUserLikes(prev => prev.filter(id => id !== itemId));
      } else {
        await updateDoc(userRef, {
          likedItems: arrayUnion(itemId)
        });
        setUserLikes(prev => [...prev, itemId]);
        
        // Create notification for item owner if it's not the current user
        if (itemOwnerId && itemOwnerId !== user.uid) {
          try {
            const notificationsRef = collection(db, 'notifications');
            await addDoc(notificationsRef, {
              type: 'like',
              fromUserId: user.uid,
              fromUsername: user.email?.split('@')[0] || 'Anonymous',
              toUserId: itemOwnerId,
              itemId: itemId,
              itemType: allItems.find(item => item.id === itemId)?.type || 'unknown',
              createdAt: new Date(),
              read: false
            });
            console.log('Created like notification for user:', itemOwnerId);
          } catch (notificationError) {
            console.error('Error creating notification:', notificationError);
          }
        }
      }
      console.log('Updated likes for item:', itemId);
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  const handleSave = async (itemId) => {
    if (!user?.uid) return;

    const isSaved = userSaved.includes(itemId);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      
      if (isSaved) {
        await updateDoc(userRef, {
          savedItems: arrayRemove(itemId)
        });
        setUserSaved(prev => prev.filter(id => id !== itemId));
      } else {
        await updateDoc(userRef, {
          savedItems: arrayUnion(itemId)
        });
        setUserSaved(prev => [...prev, itemId]);
      }
      console.log('Updated saved items for item:', itemId);
    } catch (error) {
      console.error('Error updating saved items:', error);
    }
  };

  const handleShare = async (itemId) => {
    const shareUrl = `${window.location.origin}/profiles/${username}/${itemId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this content',
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled or failed');
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderTaggedUsers = (taggedUsers) => {
    if (!taggedUsers || taggedUsers.length === 0) return null;
    
    return (
      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
          Tagged:
        </div>
        {taggedUsers.map((username, index) => (
          <Link
            key={index}
            to={`/profiles/${username}`}
            style={{
              color: '#0d47a1',
              textDecoration: 'none',
              marginRight: '12px',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            @{username}
          </Link>
        ))}
      </div>
    );
  };

  const renderKeywords = (keywords) => {
    if (!keywords || keywords.length === 0) return null;
    
    return (
      <div style={{ 
        marginTop: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {keywords.map((keyword, index) => (
          <span
            key={index}
            style={{
              background: '#f5f5f5',
              color: '#666',
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            {keyword}
          </span>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="discover-container">
        <div className="discover-main">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!allItems.length) {
    return (
      <div className="discover-container">
        <div className="discover-main">
          <p>No content found.</p>
          <p>Debug info:</p>
          <p>Username: {username}</p>
          <p>Item ID: {itemId}</p>
          <p>User data found: {userData ? 'Yes' : 'No'}</p>
          <p>Photos array length: {userData?.photos?.length || 0}</p>
          <p>Videos array length: {userData?.videos?.length || 0}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="discover-container">
      <div className="discover-main">
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
          padding: '0 20px',
          position: 'sticky',
          top: '80px',
          background: 'white',
          zIndex: 10,
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '16px'
        }}>
          <Link
            to={`/profiles/${username}`}
            style={{
              color: '#0d47a1',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            ← Back to {username}'s profile
          </Link>
          
          <div style={{ fontSize: '14px', color: '#666' }}>
            {allItems.length} items
          </div>
        </div>

        {/* Single Column Feed - Show ALL Items (excluding saved items) */}
        <div style={{ 
          maxWidth: '600px', 
          margin: '0 auto',
          padding: '0 20px'
        }}>
          {allItems.map((item, index) => {
            const isLiked = userLikes.includes(item.id);
            const isSaved = userSaved.includes(item.id);
            const isCurrentItem = index === currentIndex;
            
            return (
              <article 
                key={item.id}
                id={`item-${index}`}
                style={{
                  marginBottom: '60px',
                  background: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: isCurrentItem 
                    ? '0 8px 32px rgba(0, 0, 0, 0.15)' 
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: isCurrentItem 
                    ? '2px solid #0d47a1' 
                    : '1px solid #f0f0f0',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Post Header */}
                <div style={{ 
                  padding: '24px 28px 20px',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <Link to={`/profiles/${item.username || username}`}>
                        <div style={{
                          width: '27.2px',
                          height: '27.2px',
                          borderRadius: '50%',
                          background: '#e5e5e5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#666'
                        }}>
                          {(item.username || username).charAt(0).toUpperCase()}
                        </div>
                      </Link>
                      <div>
                        <Link 
                          to={`/profiles/${item.username || username}`}
                          style={{ 
                            textDecoration: 'none',
                            color: '#333',
                            fontWeight: 600,
                            fontSize: '16px'
                          }}
                        >
                          {item.username || username}
                        </Link>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#999',
                          marginTop: '4px'
                        }}>
                          {formatDate(item.createdAt)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Project Type Badge */}
                    {item.projectType && (
                      <span style={{
                        background: '#f0f9ff',
                        color: '#0369a1',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {item.projectType.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Image/Video */}
                <div style={{ position: 'relative' }}>
                  {item.type === 'legacy-video' ? (
                    <video
                      src={item.imageUrl}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '54.4vh',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      controls
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <img
                      src={item.imageUrl}
                      alt={item.description}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '54.4vh',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: '28px' }}>
                  {/* Actions */}
                  {user && !user.isGuest && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      marginBottom: '24px'
                    }}>
                      <button
                        onClick={() => handleLike(item.id, userData?.uid)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '24px',
                          color: isLiked ? '#e91e63' : '#666',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        {isLiked ? '♥' : '♡'}
                      </button>
                      
                      <button
                        onClick={() => handleSave(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: isSaved ? '#0d47a1' : '#666',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        {isSaved ? '★' : '☆'}
                      </button>
                      
                      <button
                        onClick={() => handleShare(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: '#666',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        ↗
                      </button>
                    </div>
                  )}

                  {/* Description */}
                  {item.description && (
                    <div style={{
                      fontSize: '16px',
                      lineHeight: '1.6',
                      color: '#333',
                      marginBottom: '20px',
                      fontFamily: 'Georgia, serif'
                    }}>
                      {item.description}
                    </div>
                  )}

                  {/* Location */}
                  {item.location && (
                    <div style={{
                      fontSize: '14px',
                      color: '#666',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      📍 {item.location}
                    </div>
                  )}

                  {/* Tagged Users */}
                  {renderTaggedUsers(item.taggedUsers)}

                  {/* Keywords */}
                  {renderKeywords(item.keywords)}

                  {/* Stats */}
                  <div style={{
                    marginTop: '24px',
                    paddingTop: '20px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      gap: '20px',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      <span>{item.likes || 0} likes</span>
                      <span>{item.views || 0} views</span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UserProfileDetail;